import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Button, Stack, Typography } from "@mui/material";
import { darken } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import NoData from "../../components/UI/NoData";
import PagesContainer from "../../components/UI/PagesContainer";
import useFetch from "../../hooks/useFetch";
import { useAppSelector } from "../../store/store";
import {
  getTicketAutomationReceivedRule,
  getTasySchedMenus,
  ITasySchedMenu,
} from "../../services/tasySchedMenu.service";

const translationPath = "page.schedules.scheduleMenus.";
const MY_SCHEDULES_AUTOMATION_CODE = "33";

const NewScheduleMenus = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { actionButtonColor, actionButtonColorDark } = useAppSelector(
    (state) => state.theme,
  );
  const [menus, setMenus] = useState<ITasySchedMenu[]>([]);

  const { sendRequest, loading } = useFetch<void, ITasySchedMenu[]>(
    getTasySchedMenus,
  );
  const {
    sendRequest: getTicketAutomationReceivedRuleRequest,
    loading: loadingRule,
  } = useFetch(getTicketAutomationReceivedRule);

  const orderedMenus = useMemo(
    () => [...menus].sort((a, b) => a.displayOrder - b.displayOrder),
    [menus],
  );

  const fetchMenus = useCallback(async () => {
    const { data, success } = await sendRequest();

    if (success && data) {
      setMenus(data);
    }
  }, [sendRequest]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  const handleMenuClick = async (menu: ITasySchedMenu) => {
    const ruleId = menu.ticketAutomationReceivedRule?.id;

    if (ruleId) {
      const { data, success } =
        await getTicketAutomationReceivedRuleRequest(ruleId);

      if (
        success &&
        data?.ticketAutomationCode === MY_SCHEDULES_AUTOMATION_CODE
      ) {
        navigate("/schedule-new/mySchedules", {
          state: {
            ticketAutomationReceivedRuleId: data.id,
            allowedAction: data.ticketAutomationReceivedRuleAllowedAction,
          },
        });
        return;
      }

      navigate(`/schedule-new/menuFlow/${menu.id}`);
      return;
    }

    navigate(`/schedule-new/menuMessage/${menu.id}`, {
      state: { customMessage: menu.customMessage },
    });
  };

  return (
    <PagesContainer
      title={t(`${translationPath}title`)}
      loading={loading || loadingRule}
    >
      {orderedMenus.length === 0 && (
        <NoData message={t(`${translationPath}noMenus`)} />
      )}

      {orderedMenus.length > 0 && (
        <Stack
          sx={(theme) => ({
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 260px))",
            gap: theme.spacing(3),
            alignItems: "stretch",
            pt: 3,
          })}
        >
          {orderedMenus.map((menu) => (
            <Button
              key={menu.id}
              variant="contained"
              onClick={() => handleMenuClick(menu)}
              endIcon={<ArrowForwardIosIcon fontSize="small" />}
              sx={(theme) => ({
                backgroundColor:
                  theme.palette.mode === "light"
                    ? actionButtonColor || theme.palette.primary.main
                    : actionButtonColorDark || theme.palette.primary.main,
                color: theme.palette.getContrastText(
                  theme.palette.mode === "light"
                    ? actionButtonColor || theme.palette.primary.main
                    : actionButtonColorDark || theme.palette.primary.main,
                ),
                minHeight: 64,
                borderRadius: 2,
                justifyContent: "space-between",
                px: 3,
                textTransform: "none",
                fontSize: 18,
                fontWeight: 700,
                overflow: "hidden",
                "& .MuiButton-endIcon": {
                  ml: 2,
                },
                "&:hover": {
                  backgroundColor: darken(
                    theme.palette.mode === "light"
                      ? actionButtonColor || theme.palette.primary.main
                      : actionButtonColorDark || theme.palette.primary.main,
                    0.12,
                  ),
                },
                [theme.breakpoints.down("sm")]: {
                  width: "100%",
                  maxWidth: "100%",
                },
              })}
            >
              <Typography
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {menu.name}
              </Typography>
            </Button>
          ))}
        </Stack>
      )}
    </PagesContainer>
  );
};

export default NewScheduleMenus;
